import type { Rating } from "../types/rating";
import { isUnknownArray, isRecord } from "../types/data";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import ru from "i18n-iso-countries/langs/ru.json";

countries.registerLocale(en);
countries.registerLocale(ru);

const COUNTRY_ALIASES = {
  "united states of america": "US",
  "united states": "US",
  usa: "US",
  us: "US",
  сша: "US",
  "соединенные штаты америки": "US",
  "united kingdom": "GB",
  "great britain": "GB",
  britain: "GB",
  england: "GB",
  uk: "GB",
  великобритания: "GB",
  "south korea": "KR",
  korea: "KR",
  "republic of korea": "KR",
  "korea republic of": "KR",
  "южная корея": "KR",
  "корея южная": "KR",
  "республика корея": "KR",
  "north korea": "KP",
  "северная корея": "KP",
  "корея северная": "KP",
  russia: "RU",
  россия: "RU",
  "russian federation": "RU",
  "российская федерация": "RU",
  "soviet union": "SU",
  ussr: "SU",
  ссср: "SU",
  "czech republic": "CZ",
  czechia: "CZ",
  czech: "CZ",
  belgium: "BE",
  бельгия: "BE",
  netherlands: "NL",
  нидерланды: "NL",
  australia: "AU",
  австралия: "AU",
  "new zealand": "NZ",
  "новая зеландия": "NZ",
  spain: "ES",
  испания: "ES",
  poland: "PL",
  польша: "PL",
  norway: "NO",
  норвегия: "NO",
  sweden: "SE",
  швеция: "SE",
  denmark: "DK",
  дания: "DK",
  finland: "FI",
  финляндия: "FI",
  ireland: "IE",
  ирландия: "IE",
  canada: "CA",
  канада: "CA",
  france: "FR",
  франция: "FR",
  italy: "IT",
  италия: "IT",
  hungary: "HU",
  венгрия: "HU",
  indonesia: "ID",
  индонезия: "ID",
  switzerland: "CH",
  швейцария: "CH",
  hongkong: "HK",
  "hong kong": "HK",
  гонконг: "HK",
  china: "CN",
  китай: "CN",
  кнр: "CN",
  taiwan: "TW",
  тайвань: "TW",
  uae: "AE",
  оаэ: "AE",
  "объединенные арабские эмираты": "AE",
  "доминиканская республика": "DO",
  kosovo: "XK",
  косово: "XK",
  "north macedonia": "MK",
  macedonia: "MK",
  "северная македония": "MK"
};

const COUNTRY_NAME_OVERRIDES: Record<"ru" | "en", Record<string, string>> = {
  ru: {
    SU: "СССР",
    XK: "Косово",
    YU: "Югославия"
  },
  en: {
    SU: "USSR",
    XK: "Kosovo",
    YU: "Yugoslavia"
  }
};

const normalizeCountryString = (value: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/&/g, " and ")
    .replace(/[().,]/g, "")
    .replace(/\s+/g, " ");

const titleCase = (value: string) =>
  normalizeCountryString(value)
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const COUNTRY_CODE_BY_NAME = new Map(Object.entries(COUNTRY_ALIASES));

["en", "ru"].forEach((locale) => {
  Object.entries(countries.getNames(locale)).forEach(([code, name]) => {
    COUNTRY_CODE_BY_NAME.set(normalizeCountryString(name), code);
  });
});

const isTechnicalCountryValue = (value: string) => /^(undefined|null|nan)$/i.test(String(value || "").trim());

const getRawCountryValue = (country: unknown) => {
  if (!country) return "";
  const candidates = typeof country === "string"
    ? [country]
    : isRecord(country) ? [
        country.iso_3166_1,
        country.countryCode,
        country.code,
        country.name,
        country.english_name,
        country.nativeName
      ] : [];

  return candidates
    .map((value) => String(value || "").trim())
    .find((value) => value && !isTechnicalCountryValue(value)) || "";
};

const normalizeLocale = (locale = "ru") =>
  String(locale).toLowerCase().startsWith("en") ? "en" : "ru";

export const resolveCountry = (country: unknown, locale = "ru") => {
  const normalizedLocale = normalizeLocale(locale);
  const rawValue = getRawCountryValue(country);
  if (!rawValue || isTechnicalCountryValue(rawValue)) return null;

  const normalized = normalizeCountryString(rawValue);
  const explicitCode =
    isRecord(country)
      ? country.iso_3166_1 || country.countryCode || country.code
      : null;
  const alpha2Code =
    (typeof explicitCode === "string" && explicitCode.length === 2 ? explicitCode : null) ||
    (rawValue.length === 2 ? rawValue : null) ||
    COUNTRY_CODE_BY_NAME.get(normalized) ||
    countries.getAlpha2Code(rawValue, "en") ||
    countries.getAlpha2Code(rawValue, "ru") ||
    countries.getAlpha2Code(titleCase(rawValue), "en") ||
    countries.getAlpha2Code(titleCase(rawValue), "ru") ||
    null;

  const code = alpha2Code ? String(alpha2Code).toUpperCase() : null;
  const name = code
    ? COUNTRY_NAME_OVERRIDES[normalizedLocale]?.[code] || countries.getName(code, normalizedLocale)
    : null;

  return {
    code,
    name: name || rawValue
  };
};

export const getCountryName = (country: unknown, locale = "ru") => resolveCountry(country, locale)?.name || "";

export const getCountryCode = (country: unknown) => resolveCountry(country)?.code || "";

export const getCountryNames = (countriesList: unknown = [], locale = "ru") => {
  const values = isUnknownArray(countriesList) ? countriesList : [countriesList];
  const seen = new Set<string>();
  return values
    .map((country) => resolveCountry(country, locale))
    .filter((country): country is NonNullable<typeof country> => country !== null)
    .filter(({ name, code }) => {
      const key = code || name;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ name }) => name);
};

export const getRatingCountryNames = (rating: Rating = {}, locale = "ru") =>
  getCountryNames([
    ...(isUnknownArray(rating.originCountry) ? rating.originCountry : rating.originCountry ? [rating.originCountry] : []),
    ...(isUnknownArray(rating.origin_country) ? rating.origin_country : rating.origin_country ? [rating.origin_country] : []),
    ...(isUnknownArray(rating.productionCountry) ? rating.productionCountry : rating.productionCountry ? [rating.productionCountry] : []),
    ...(isUnknownArray(rating.productionCountries) ? rating.productionCountries : rating.productionCountries ? [rating.productionCountries] : []),
    ...(isUnknownArray(rating.production_countries) ? rating.production_countries : rating.production_countries ? [rating.production_countries] : [])
  ], locale);
